import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Middle Stage Self Assessment Viewer Component (READ-ONLY)
 * Designed to match the UI in Image 1 (Student's Self Reflection)
 *
 * @param {Object} submission - The submission object containing self_assessment data
 */
const MiddleStageSelfAssessmentViewer = ({ submission }) => {
    // If no self-assessment data is available, show placeholder
    if (!submission || !submission.self_assessment) {
        return (
            <div className="bg-white font-[poppins] shadow-sm rounded-lg overflow-hidden mb-6 border border-orange-200">
                <div className="px-4 py-3 bg-orange-500 text-white">
                    <h2 className="text-lg font-medium text-center uppercase">Student's Self Reflection</h2>
                </div>
                <div className="p-6 text-center">
                    <AlertCircle className="h-12 w-12 text-orange-200 mx-auto mb-3" />
                    <p className="text-gray-500">No self-assessment has been submitted yet.</p>
                </div>
            </div>
        );
    }

    // Response options for the reflection questions
    const responseOptions = [
        { value: 'Yes', label: 'Yes', emoji: '😊👍' },
        { value: 'To an extent', label: 'To an extent', emoji: '😐👉' },
        { value: 'No', label: 'No', emoji: '😔👎' },
        { value: 'Not sure', label: 'Not sure', emoji: '🤔' }
    ];

    // Helper function to check if an option is selected
    const isOptionSelected = (questionKey, optionValue) => {
        return submission.self_assessment.self_reflection &&
               submission.self_assessment.self_reflection[questionKey] === optionValue;
    };

    // Helper function to check if a grid item is selected
    const isGridItemSelected = (category, itemText) => {
        return submission.self_assessment.progress_grid &&
               Array.isArray(submission.self_assessment.progress_grid[category]) &&
               submission.self_assessment.progress_grid[category].includes(itemText);
    };

    // Progress grid categories and items
    const progressGrid = {
        awareness: [
            { text: 'I was able to learn something new.' },
            { text: 'I was attentive to every detail of the activity.' },
            { text: 'I was able to understand the activity.' },
            { text: 'I was able to focus and engage with the activity.' },
            { text: 'I was able to follow the instructions.' },
            { text: 'I was able to find purpose and meaning in the activity.' }
        ],
        sensitivity: [
            { text: 'I was able to understand and express my emotions.' },
            { text: 'I was able to motivate myself & my peer when things were difficult.' },
            { text: 'I was able to understand the emotions of my peer.' },
            { text: 'I was able to seek and use support from my peers and teacher.' },
            { text: 'I was able to contribute individually or as a group member.' },
            { text: 'I was able to help others in some way.' }
        ],
        creativity: [
            { text: 'I was curious to explore and learn new things during the activity.' },
            { text: 'I was able to think of \'out of the box\' solutions.' },
            { text: 'I was able to think of new ways to do the activity.' },
            { text: 'I was able to express my creativity while doing the activity.' },
            { text: 'I was able to generate innovative ideas.' },
            { text: 'I was able to take calculated risks.' }
        ]
    };

    // Count selected items in each category
    const getSelectedCount = (category) => {
        if (!submission.self_assessment.progress_grid ||
            !Array.isArray(submission.self_assessment.progress_grid[category])) {
            return 0;
        }
        return submission.self_assessment.progress_grid[category].length;
    };

    return (
        <div className="bg-white font-[poppins] shadow-sm rounded-lg overflow-hidden mb-6 border border-gray-200">
            {/* Header */}
            <div className="px-4 py-3 bg-orange-100">
                <h2 className="text-lg font-medium text-gray-900 text-center uppercase">
                    Student's Self Reflection
                </h2>
            </div>

            <div className="p-4">
                {/* Reflection Questions Section */}
                <div className="mb-6">
                    <p className="text-sm font-medium text-gray-700 mb-4">
                        Based on your experience of the activity, please circle the response that is applicable.
                    </p>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <tbody>
                                {/* Question 1 */}
                                <tr className="divide-x divide-gray-200">
                                    <td className="px-4 py-3 text-sm text-gray-900 w-2/5">
                                        I am proud of myself and my effort.
                                    </td>
                                    {responseOptions.map((option) => (
                                        <td key={option.value} className={`px-4 py-3 text-center ${isOptionSelected('proud_of_effort', option.value) ? 'bg-orange-50' : ''}`}>
                                            <div className="flex flex-col items-center">
                                                <div className={`text-2xl mb-1 ${isOptionSelected('proud_of_effort', option.value) ? 'text-orange-500' : 'text-gray-300'}`}>
                                                    {option.emoji}
                                                </div>
                                                <span className="text-xs">{option.label}</span>
                                            </div>
                                        </td>
                                    ))}
                                </tr>

                                {/* Question 2 */}
                                <tr className="divide-x divide-gray-200 bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                        I will be able to apply what I learnt from this activity to real life situations.
                                    </td>
                                    {responseOptions.map((option) => (
                                        <td key={option.value} className={`px-4 py-3 text-center ${isOptionSelected('apply_to_real_life', option.value) ? 'bg-orange-50' : ''}`}>
                                            <div className="flex flex-col items-center">
                                                <div className={`text-2xl mb-1 ${isOptionSelected('apply_to_real_life', option.value) ? 'text-orange-500' : 'text-gray-300'}`}>
                                                    {option.emoji}
                                                </div>
                                                <span className="text-xs">{option.label}</span>
                                            </div>
                                        </td>
                                    ))}
                                </tr>

                                {/* Question 3 */}
                                <tr className="divide-x divide-gray-200">
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                        I am motivated to learn further about the concepts covered in the activity.
                                    </td>
                                    {responseOptions.map((option) => (
                                        <td key={option.value} className={`px-4 py-3 text-center ${isOptionSelected('motivated_to_learn_more', option.value) ? 'bg-orange-50' : ''}`}>
                                            <div className="flex flex-col items-center">
                                                <div className={`text-2xl mb-1 ${isOptionSelected('motivated_to_learn_more', option.value) ? 'text-orange-500' : 'text-gray-300'}`}>
                                                    {option.emoji}
                                                </div>
                                                <span className="text-xs">{option.label}</span>
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Progress Grid Section */}
                <div className="mb-6">
                    <div className="bg-orange-100 py-2 px-4 text-center mb-4">
                        <h3 className="font-medium text-gray-900">My Progress Grid</h3>
                    </div>

                    <p className="text-sm text-gray-700 mb-4">
                        Below are a few statements. Read each one of them carefully and circle the ones which are true
                        based on your performance on the activity.
                    </p>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr className="divide-x divide-gray-200">
                                    <th scope="col" className="px-4 py-3 text-center text-sm font-medium text-gray-900 bg-pink-100 w-1/3">
                                        Awareness
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-center text-sm font-medium text-gray-900 bg-green-100 w-1/3">
                                        Sensitivity
                                    </th>
                                    <th scope="col" className="px-4 py-3 text-center text-sm font-medium text-gray-900 bg-blue-100 w-1/3">
                                        Creativity
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {/* Create rows based on the maximum number of items in any category */}
                                {Array.from({ length: Math.max(
                                    progressGrid.awareness.length,
                                    progressGrid.sensitivity.length,
                                    progressGrid.creativity.length
                                )}).map((_, rowIndex) => (
                                    <tr key={rowIndex} className={`divide-x divide-gray-200 ${rowIndex % 2 === 1 ? 'bg-gray-50' : ''}`}>
                                        <td className="px-4 py-2">
                                            {progressGrid.awareness[rowIndex] && (
                                                <div className="flex items-start">
                                                    <div className={`flex-shrink-0 h-5 w-5 mt-0.5 ${
                                                        isGridItemSelected('awareness', progressGrid.awareness[rowIndex].text) 
                                                            ? 'text-green-500' 
                                                            : 'text-gray-300'
                                                    }`}>
                                                        {isGridItemSelected('awareness', progressGrid.awareness[rowIndex].text) ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span className={`ml-2 text-xs ${isGridItemSelected('awareness', progressGrid.awareness[rowIndex].text) ? 'font-medium' : ''}`}>
                                                        {progressGrid.awareness[rowIndex].text}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2">
                                            {progressGrid.sensitivity[rowIndex] && (
                                                <div className="flex items-start">
                                                    <div className={`flex-shrink-0 h-5 w-5 mt-0.5 ${
                                                        isGridItemSelected('sensitivity', progressGrid.sensitivity[rowIndex].text) 
                                                            ? 'text-green-500' 
                                                            : 'text-gray-300'
                                                    }`}>
                                                        {isGridItemSelected('sensitivity', progressGrid.sensitivity[rowIndex].text) ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span className={`ml-2 text-xs ${isGridItemSelected('sensitivity', progressGrid.sensitivity[rowIndex].text) ? 'font-medium' : ''}`}>
                                                        {progressGrid.sensitivity[rowIndex].text}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2">
                                            {progressGrid.creativity[rowIndex] && (
                                                <div className="flex items-start">
                                                    <div className={`flex-shrink-0 h-5 w-5 mt-0.5 ${
                                                        isGridItemSelected('creativity', progressGrid.creativity[rowIndex].text) 
                                                            ? 'text-green-500' 
                                                            : 'text-gray-300'
                                                    }`}>
                                                        {isGridItemSelected('creativity', progressGrid.creativity[rowIndex].text) ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        ) : (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span className={`ml-2 text-xs ${isGridItemSelected('creativity', progressGrid.creativity[rowIndex].text) ? 'font-medium' : ''}`}>
                                                        {progressGrid.creativity[rowIndex].text}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}

                                {/* Teacher Use Only Row */}
                                <tr className="divide-x divide-gray-200">
                                    <td colSpan="3" className="px-4 py-2 text-sm font-medium text-gray-700 text-center">
                                        (For Teacher's Use Only)
                                    </td>
                                </tr>
                                <tr className="divide-x divide-gray-200 bg-gray-50">
                                    <td className="px-4 py-2 text-sm">
                                        No. of statements circled for Awareness: <span className="font-medium">{getSelectedCount('awareness')}</span>
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        No. of statements circled for Sensitivity: <span className="font-medium">{getSelectedCount('sensitivity')}</span>
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        No. of statements circled for Creativity: <span className="font-medium">{getSelectedCount('creativity')}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* My Learnings Section */}
                <div className="mb-6">
                    <div className="bg-orange-100 py-2 px-4 text-center mb-4">
                        <h3 className="font-medium text-gray-900">My Learnings</h3>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                By doing this activity, I learnt
                            </label>
                            <div className="shadow-sm bg-gray-50 p-3 rounded-md border border-gray-300 min-h-16">
                                {submission.self_assessment.learnings?.insights || "Not provided"}
                            </div>
                            <p className="mt-1 text-xs text-gray-500 italic">
                                (Use this space to write your reflections/insights from the activity)
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                The most interesting thing about this activity was
                            </label>
                            <div className="shadow-sm bg-gray-50 p-3 rounded-md border border-gray-300 min-h-12">
                                {submission.self_assessment.learnings?.most_interesting || "Not provided"}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    I need practice on
                                </label>
                                <div className="shadow-sm bg-gray-50 p-3 rounded-md border border-gray-300">
                                    {submission.self_assessment.learnings?.need_practice || "Not provided"}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    I need help with
                                </label>
                                <div className="shadow-sm bg-gray-50 p-3 rounded-md border border-gray-300">
                                    {submission.self_assessment.learnings?.need_help || "Not provided"}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MiddleStageSelfAssessmentViewer;